import { useState, type ImgHTMLAttributes } from "react";
import { cn } from "@/lib/utils";
import { FALLBACK_IMAGE } from "@/lib/article";

interface OptimizedImageProps extends Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "loading"
> {
  src: string;
  alt: string;
  /** Show a soft skeleton until the image loads */
  showPlaceholder?: boolean;
  /** Override the default lazy loading (e.g., for above-the-fold hero images) */
  priority?: boolean;
  /** Aspect ratio class to keep layout stable while loading */
  aspectClassName?: string;
}

/**
 * Responsive, lazy-loaded image with placeholder + smooth fade-in.
 * Supabase Storage URLs are augmented with `?width=` query params via srcSet
 * so the browser fetches the smallest sufficient image (CDN-friendly).
 */
export function OptimizedImage({
  src,
  alt,
  className,
  showPlaceholder = true,
  priority = false,
  aspectClassName,
  width,
  height,
  sizes = "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw",
  ...rest
}: OptimizedImageProps) {
  const requestedSrc = src || FALLBACK_IMAGE;
  const [state, setState] = useState(() => ({
    requestedSrc,
    imgSrc: requestedSrc,
    loaded: false,
  }));
  const imgSrc =
    state.requestedSrc === requestedSrc ? state.imgSrc : requestedSrc;
  const loaded = state.requestedSrc === requestedSrc ? state.loaded : false;

  // Build a srcSet for CDN transform-friendly URLs.
  const buildSrcSet = (s: string): string | undefined => {
    if (!s) return undefined;
    const widths = [128, 256, 320, 400, 500, 640, 800, 960, 1200, 1600];
    if (/\/storage\/v1\/object\/public\//.test(s)) {
      return widths
        .map((w) => {
          const cleanUrl = s.replace(/^https?:\/\//, "");
          return `https://wsrv.nl/?url=${encodeURIComponent(cleanUrl)}&w=${w}&output=webp ${w}w`;
        })
        .join(", ");
    }
    if (/images\.unsplash\.com/.test(s)) {
      return widths
        .map(
          (w) =>
            `${s}${s.includes("?") ? "&" : "?"}w=${w}&auto=format&fit=crop ${w}w`,
        )
        .join(", ");
    }
    if (/\.s3\.(ap-south-1\.)?amazonaws\.com\//.test(s)) {
      return widths
        .map((w) => {
          const cleanUrl = s.replace(/^https?:\/\//, "");
          return `https://wsrv.nl/?url=${encodeURIComponent(cleanUrl)}&w=${w}&output=webp ${w}w`;
        })
        .join(", ");
    }
    return undefined;
  };

  const srcSet = buildSrcSet(imgSrc);

  return (
    <div className={cn("relative overflow-hidden", aspectClassName)}>
      {showPlaceholder && !loaded && (
        <div className="absolute inset-0 bg-muted animate-pulse" aria-hidden />
      )}
      <img
        src={imgSrc}
        srcSet={srcSet}
        sizes={srcSet ? sizes : undefined}
        alt={alt}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={priority ? "high" : "auto"}
        width={width}
        height={height}
        onLoad={() => setState({ requestedSrc, imgSrc, loaded: true })}
        onError={() => {
          setState({ requestedSrc, imgSrc: FALLBACK_IMAGE, loaded: true });
        }}
        className={cn(
          "transition-opacity duration-500",
          loaded ? "opacity-100" : "opacity-0",
          className,
        )}
        {...rest}
      />
    </div>
  );
}
